import type { RadioGroupContextValue } from '@radix-ui/react-radio-group';
import { isEmpty } from 'ramda';
import * as React from 'react';

import { Button, ChoiceListItem, Icon, RadixRadioGroupRoot } from '@sunwebgroup/ui-components';

import { useSelectFilter } from '@/scopes/search/search/domain/hooks';
import { useIntl } from '@/intl';
import FilterGroup from './FilterGroup';
import { ShowMore } from '../../../../../../common/components/ShowMore';
import { createSearchFilterClickEvent } from '../../../../domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';

export function NumberOfRoomsFilter() {
  const intl = useIntl();
  const { sendEvent } = useAnalytics();
  const { isFetching, options, selectedValue, setSelection } = useSelectFilter('RoomCount');

  const handleFilterChange: RadioGroupContextValue['onValueChange'] = (value) => {
    setSelection(value);
    sendEvent(
      createSearchFilterClickEvent({
        element: 'radio button',
        type: 'number of rooms',
        result: value,
      })
    );
  };

  const handleButtonClick = React.useCallback(() => {
    setSelection(null);
  }, [setSelection]);

  if (isEmpty(options)) return null;

  return (
    <FilterGroup title={intl.formatMessage({ id: 'number_of_rooms_label' })} defaultOpen={selectedValue !== null}>
      {/* Null value is not accepted, but makes it possible to reset the radio group. See also this open issue: https://github.com/radix-ui/primitives/issues/1588. */}
      <RadixRadioGroupRoot value={selectedValue ?? null} onValueChange={handleFilterChange}>
        <ShowMore
          openLabel={intl.formatMessage({ id: 'show_more' })}
          closeLabel={intl.formatMessage({ id: 'show_less' })}
          visibleItems={4}
        >
          {options.map(({ name, value, resultsCount }) => (
            <ChoiceListItem
              choiceListItemType="radio"
              key={value}
              value={value}
              checked={selectedValue === value}
              label={name}
              quantity={resultsCount}
              icon="IconBed"
              disabled={isFetching}
            />
          ))}
        </ShowMore>
        {selectedValue && !isFetching && (
          <Button $hierarchy="tertiary" $withoutColor onClick={handleButtonClick} $fullSize>
            <Icon glyph="IconCrossMedium" />
            {intl.formatMessage({ id: 'search_filter_remove_selection' })}
          </Button>
        )}
      </RadixRadioGroupRoot>
    </FilterGroup>
  );
}
